import * as React from 'react'
import { useMemo, useState } from 'react'
import * as deepEqual from 'react-fast-compare'
import { DomainDto } from '@halo/dtos/domain.dto'
import { Button, Drawer, Well } from '@pushly/aqe/lib/components'
import {
    BarChartOutlined,
    CopyOutlined,
    EditOutlined,
    GlobalOutlined,
    InfoCircleOutlined,
    WarningOutlined,
} from '@ant-design/icons'
import { Divider, Flex, Form, Input, Modal, notification, Select, Skeleton } from 'antd'
import { classnames } from '@halo/_utils/classnames'
import moment from 'moment-timezone'
import { simpleNotification } from '@halo/_utils/notifications'
import { simpleFormErrorNotification } from '@pushly/aqe/lib/utils'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { DomainService } from '@halo/services/domain.service'
import { Container } from 'typescript-ioc/es5'
import {
    DEFAULT_DOMAIN_INDUSTRY_ID,
    DEFAULT_DRAWER_FORM_ITEM_LAYOUT,
    FEAT_SDK_EVENT_ONLY_DOMAINS,
    SETTINGS_WELL_FORM_LAYOUT,
} from '@halo/constants'
import { Link } from 'react-router-dom'
import * as randomstring from 'randomstring'
import { prepareWhitelistDomains } from '@halo/_utils/domain'
import './style/domain-data-form.scss'
import ColorPickerPopover from '@halo/components/color-picker-popover/color-picker-popover'
import { escapeRegExp } from '@halo/_utils/regexp'
import { Flags } from '@pushly/aquarium-utils/lib/enums/flags'
import TestDomainOverlay from '@halo/components/test-domain-overlay/test-domain-overlay'
import GenerateTestDomainModal from '@halo/components/generate-test-domain-modal/generate-test-domain-modal'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { asCaslSubject } from '@halo/stores/ability.state'
import { SubjectEntity } from '@halo/enums/ability-entity.enum'
import { ApiEnumService } from '@halo/services/api-enum.service'
import { Industry } from '@pushly/models/lib/structs/industry'

interface IDomainDetailsEditor {
    domain: DomainDto
    visible: boolean
    onClose: (e: any) => void
    onDomainUpdated: (domain: Partial<DomainDto>) => void
    industries: Industry[]
}

interface IDomainDetailsEditorFormValues {
    prefix?: string
    displayName?: string
    name?: string
    industryId?: number
    overlayColor?: string
    timezone?: string
    whitelistDomains?: string[]
    sdkEventOnlyDomains?: string[]
}

const computeChangedDomainValues = (
    domain: DomainDto,
    formValues: IDomainDetailsEditorFormValues,
): Partial<DomainDto> => {
    const values: Partial<DomainDto> = {}

    let display_meta = { ...(domain.display_meta ?? {}) }

    // display_meta update, should contain a prefix regardless of update
    if (
        formValues.overlayColor !== domain.display_meta?.overlay_config?.color &&
        formValues.overlayColor !== undefined
    ) {
        display_meta = {
            ...display_meta,
            overlay_config: {
                color: formValues.overlayColor,
            },
        }
    }

    if (!!formValues.prefix) {
        display_meta = {
            ...display_meta,
            prefix: formValues.prefix,
        }
    }

    if (!deepEqual(display_meta, domain.display_meta ?? {})) {
        values.display_meta = display_meta
    }

    if (formValues.displayName !== undefined && formValues.displayName !== domain.display_name) {
        values.display_name = formValues.displayName
    }

    if (formValues.name !== undefined && formValues.name !== domain.name) {
        values.name = formValues.name
    }

    if (formValues.timezone !== undefined && formValues.timezone !== domain.timezone) {
        values.timezone = formValues.timezone
    }

    if (
        formValues.whitelistDomains !== undefined &&
        !deepEqual(formValues.whitelistDomains, domain.whitelist_domains || [])
    ) {
        values.whitelist_domains = prepareWhitelistDomains(formValues.whitelistDomains)
    }

    const domainHasEventOnlyDomainsFlag = domain.flags?.includes(FEAT_SDK_EVENT_ONLY_DOMAINS) ?? false
    if (domainHasEventOnlyDomainsFlag) {
        if (
            formValues.sdkEventOnlyDomains !== undefined &&
            !deepEqual(formValues.sdkEventOnlyDomains, domain.sdk_event_only_domains)
        ) {
            values.sdk_event_only_domains = formValues.sdkEventOnlyDomains
        }
    }

    if (formValues.industryId !== undefined && formValues.industryId !== domain.industry_id) {
        values.industry_id = formValues.industryId
    }

    return values
}

export const DomainDetailsEditor: React.FC<IDomainDetailsEditor> = (props) => {
    const { domain, industries, visible, onClose, onDomainUpdated } = props

    const appService = useMemo(() => Container.get(AppService), [])
    const domainService = useMemo(() => Container.get(DomainService), [])

    const [form] = Form.useForm<IDomainDetailsEditorFormValues>()

    const [color, setColor] = useState<string>(domain?.display_meta?.overlay_config?.color ?? '#ffbf00')
    const [prefixState, setPrefixState] = useState<string>(domain?.display_meta?.prefix ?? 'TEST')

    // Subscribe to value changes so the wrapping Drawer's disableSubmit recomputes as the user edits.
    Form.useWatch([], form)

    const domainHasEventOnlyDomainsFlag = domain.flags?.includes(FEAT_SDK_EVENT_ONLY_DOMAINS) ?? false

    const timezones = moment.tz.names()
    const suggestedTimezone = moment.tz.guess()

    const name = domain.name
    let displayName = domain.display_name
    const industryId = domain.industry_id ?? DEFAULT_DOMAIN_INDUSTRY_ID
    const prefix = domain?.display_meta?.prefix

    if (domain?.prod_domain_id !== null && !!prefix) {
        const rgx = new RegExp(`^\\(${escapeRegExp(prefix)}\\)\\s`, 'i')
        displayName = displayName.replace(rgx, '')
    }

    const currentValues = computeChangedDomainValues(domain, form.getFieldsValue())

    const handleSubmit = async () => {
        try {
            const values = computeChangedDomainValues(domain, form.getFieldsValue())

            const { ok, data } = await domainService.patch(domain.id, values, {
                showLoadingScreen: true,
                cancellationKey: `patch-domain-${domain.id}`,
            })

            if (ok) {
                onDomainUpdated(data)
                onClose(null)
            }
        } catch (err) {
            simpleFormErrorNotification(err)
        }
    }

    return (
        <Drawer
            className={classnames('ddf-details-editor')}
            title="Update Details"
            placement="right"
            closable={true}
            width={800}
            onSubmit={async () => {
                try {
                    await form.validateFields()
                    await handleSubmit()
                } catch (err) {
                    simpleFormErrorNotification(err)
                }
            }}
            disableSubmit={Object.keys(currentValues).length === 0}
            onClose={onClose}
            open={visible}
        >
            <Form
                form={form}
                {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                onFinish={handleSubmit}
                size="small"
                labelAlign="left"
                requiredMark={false}
            >
                <Well title="Domain Information" mode="ghost" footerMode="hidden" padding="none">
                    <Form.Item label="Display Name" required={!domain.prod_domain_id}>
                        {domain.prod_domain_id ? (
                            <Input.Group compact={true}>
                                <Form.Item name="prefix" initialValue={prefix || 'TEST'} noStyle={true}>
                                    <Select
                                        onSelect={(value: string) => {
                                            setPrefixState(value)
                                            form.setFieldsValue({ prefix: value })
                                        }}
                                    >
                                        <Select.Option value="TEST">Test</Select.Option>
                                        <Select.Option value="DEV">Dev</Select.Option>
                                        <Select.Option value="QA">QA</Select.Option>
                                    </Select>
                                </Form.Item>
                                <Input value={displayName} disabled={true} />
                            </Input.Group>
                        ) : (
                            <Form.Item
                                name="displayName"
                                initialValue={displayName}
                                rules={[
                                    {
                                        required: true,
                                        message: 'Display name is required.',
                                    },
                                ]}
                                noStyle={true}
                            >
                                <Input />
                            </Form.Item>
                        )}
                    </Form.Item>

                    <Form.Item
                        label="Domain Name"
                        name="name"
                        initialValue={name}
                        required={true}
                        rules={[
                            {
                                required: true,
                                message: 'Name is required.',
                            },
                        ]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Industry"
                        name="industryId"
                        initialValue={industryId}
                        required={true}
                        rules={[
                            {
                                required: true,
                                message: 'Industry is required.',
                            },
                        ]}
                    >
                        <Select
                            showSearch={true}
                            placeholder="Select an Industry"
                            optionFilterProp="title"
                        >
                            {industries.map((industry: Industry) => (
                                <Select.Option
                                    key={industry.id}
                                    value={industry.id}
                                    title={`${industry.name}`}
                                >
                                    {industry.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {domain.prod_domain_id && (
                        <Form.Item label="Icon Overlay Color">
                            <Flex gap={8} align="center">
                                <Form.Item
                                    name="overlayColor"
                                    initialValue={domain.display_meta?.overlayConfig?.color}
                                    noStyle={true}
                                >
                                    <ColorPickerPopover
                                        defaultColor={color}
                                        color={color}
                                        onChange={(c) => setColor(c)}
                                    />
                                </Form.Item>

                                <TestDomainOverlay
                                    label={prefixState}
                                    color={color}
                                    icon={domain.default_icon_url}
                                />
                            </Flex>
                        </Form.Item>
                    )}
                </Well>

                <Divider />

                <Well title="Platform Configuration" mode="ghost" footerMode="hidden" padding="none">
                    <Form.Item
                        label="Time Zone"
                        name="timezone"
                        initialValue={domain.timezone || suggestedTimezone}
                        required={true}
                        rules={[
                            {
                                required: true,
                                message: 'Time Zone must be selected',
                            },
                        ]}
                        extra={
                            <span>
                                <InfoCircleOutlined /> Modifying the time zone on a domain that already has
                                acquisition or delivery stats may affect historical reporting. Historical reporting
                                will continue to be reported in the time zone in which the domain was set to when
                                the data was received and any new data coming in will be attributed to the correct
                                time zone.
                            </span>
                        }
                    >
                        <Select showSearch={true} placeholder="Select a time zone">
                            {timezones.map((tz: string) => (
                                <Select.Option key={tz} value={tz}>
                                    {tz}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="SDK Allowed Domains"
                        name="whitelistDomains"
                        initialValue={domain.whitelist_domains || []}
                    >
                        <Select
                            mode="tags"
                            className="domains-allowed stacked-tags"
                            popupClassName="disabled whitelist-domains-dropdown"
                            placeholder="https://www.pushly.com, https://subdomain.pushly.com"
                            getPopupContainer={appService.getAppContainer}
                            tokenSeparators={[',', ';', ' ', '\n']}
                            notFoundContent=""
                        />
                    </Form.Item>

                    {domainHasEventOnlyDomainsFlag && (
                        <Form.Item
                            label="SDK Event Only Domains"
                            name="sdkEventOnlyDomains"
                            initialValue={domain.sdk_event_only_domains}
                        >
                            <Select
                                mode="tags"
                                className="domains-allowed stacked-tags"
                                popupClassName="disabled whitelist-domains-dropdown"
                                placeholder="https://www.pushly.com, https://subdomain.pushly.com"
                                getPopupContainer={appService.getAppContainer}
                                tokenSeparators={[',', ';', ' ', '\n']}
                                notFoundContent=""
                            />
                        </Form.Item>
                    )}
                </Well>
            </Form>
        </Drawer>
    )
}

interface IAccountDetailsWell {
    loading?: boolean
    domain: DomainDto
    title?: string
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

interface IDWState {
    showDrawer: boolean
    drawerKey?: string
    showTestForm: boolean
    industries: Industry[]
}

export class DomainDetailsWell extends React.Component<IAccountDetailsWell, IDWState> {
    public state: IDWState = {
        showDrawer: false,
        showTestForm: false,
        industries: [],
    }

    protected appState: AppState
    protected appService: AppService
    protected domainService: DomainService
    protected apiEnumService: ApiEnumService

    public constructor(props: IAccountDetailsWell) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.domainService = Container.get(DomainService)
        this.apiEnumService = Container.get(ApiEnumService)
    }

    public async componentDidMount() {
        await this.fetchAllIndustries()
    }

    public render() {
        return (
            <Well
                className="type-3 nested"
                title="Details"
                actions={
                    this.appState.abilityStore.can(
                        AbilityAction.UPDATE,
                        asCaslSubject(SubjectEntity.DOMAIN, {
                            ...this.props.domain,
                            statusId: this.props.domain?.status_id,
                        }),
                    ) && this.buildActions()
                }
                hideFooter={true}
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            <Form
                                {...SETTINGS_WELL_FORM_LAYOUT}
                                colon={false}
                                requiredMark={false}
                                labelAlign="left"
                                size="small"
                            >
                                {this.renderOrgWell()}
                                {this.renderInformationWell()}
                                {this.renderConfigurationWell()}
                            </Form>

                            <DomainDetailsEditor
                                key={this.state.drawerKey}
                                visible={this.state.showDrawer}
                                domain={this.domain}
                                onClose={this.handleDrawerClose}
                                onDomainUpdated={this.props.onDomainUpdated}
                                industries={this.state.industries}
                            />

                            <GenerateTestDomainModal
                                domain={this.domain}
                                loading={this.props.loading}
                                onOk={this.handleTestDomainCreate}
                                visible={this.state.showTestForm}
                                onCancel={this.hideNewTestForm}
                            />
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected buildActions(): React.ReactNode {
        const domain = this.props.domain
        const domainFlags = domain?.flags ?? []
        const actions = []

        const addActionDividerIfNecessary = (nodes: any[]) => {
            if (nodes.length > 0) {
                nodes.push(<span key="spacer1" className="actions-spacer" />)
            }
        }

        if (domain) {
            const demoIdRgx = /DEMO_ID_(?<demo_id>[\d]+)/i
            const flagMatch = demoIdRgx.exec(domainFlags.join(','))
            const demoDomainId = flagMatch?.groups?.demo_id

            const isDemoDomain = domainFlags.includes(Flags.domain.DEMO.key) && !!demoDomainId
            const isNewDemoDomain = domain.is_demo === true
            const isProdDomain = domain.prod_domain_id === null || domain.prod_domain_id === undefined

            // Test domain generation should not be available to internal DEMO domains
            if (isProdDomain && !isDemoDomain && !isNewDemoDomain) {
                actions.push(
                    <span key="activate">
                        <Button
                            size="small"
                            onClick={this.showNewTestForm}
                            disabled={this.props.loading}
                            icon={<CopyOutlined />}
                        >
                            <span>Generate Test Domain</span>
                        </Button>
                    </span>,
                )
            }

            // New-style demo domain — single Refresh button
            if (isNewDemoDomain && !domain.is_demo_template) {
                addActionDividerIfNecessary(actions)
                actions.push(
                    <span key="reclone">
                        <Button
                            size="small"
                            onClick={() => this.reclone()}
                            disabled={this.props.loading}
                            icon={<GlobalOutlined />}
                        >
                            <span>Refresh Demo Domain</span>
                        </Button>
                    </span>,
                )
                actions.push(
                    <span key="reseed-stats">
                        <Button
                            size="small"
                            onClick={() => this.reseedStats()}
                            disabled={this.props.loading}
                            icon={<BarChartOutlined />}
                        >
                            <span>Refresh Demo Stats</span>
                        </Button>
                    </span>,
                )
            }

            // Legacy demo domain — keep old reset buttons
            if (isDemoDomain && !isNewDemoDomain) {
                addActionDividerIfNecessary(actions)

                actions.push(
                    <span key="reset-stats">
                        <Button
                            size="small"
                            onClick={() => this.resetStats(demoDomainId)}
                            disabled={this.props.loading}
                            icon={<BarChartOutlined />}
                        >
                            <span>Reset Stats</span>
                        </Button>
                    </span>,
                )

                actions.push(
                    <span key="reset-full">
                        <Button
                            size="small"
                            onClick={() => this.resetFull(demoDomainId)}
                            disabled={this.props.loading}
                            icon={<GlobalOutlined />}
                        >
                            <span>Reset Domain</span>
                        </Button>
                    </span>,
                )
            }
        }

        addActionDividerIfNecessary(actions)

        actions.push(
            <Button
                key="edit"
                size="small"
                onClick={this.handleDrawerOpen}
                disabled={this.props.loading}
                icon={<EditOutlined />}
            >
                <span>Edit</span>
            </Button>,
        )

        return actions
    }

    protected renderOrgWell() {
        return (
            <Well.Section title="Organization">
                <Form.Item label="Org Name">
                    <Link to={`/organizations/${this.domain.account_id}`}>{this.domain.account_name}</Link>
                </Form.Item>
            </Well.Section>
        )
    }

    protected renderInformationWell() {
        return (
            <Well.Section title="Domain Information">
                <Form.Item label="Display Name">{this.domain.display_name}</Form.Item>

                <Form.Item label="Integration Type">Native</Form.Item>

                <Form.Item label="Domain Name">
                    <a href={`https://${this.domain.name}`} target="_blank">
                        {this.domain.name}
                    </a>
                </Form.Item>

                <Form.Item label="SDK Key">{this.domain.domain_key ?? ''}</Form.Item>

                <Form.Item label="Industry">{this.domain.industry?.name ?? 'Uncategorized'}</Form.Item>

                <Form.Item label="Created">
                    {moment(this.domain.created_at).format('LLL')}
                    <span className="lowlight"> by </span>
                    {this.domain.created_by_user_name}
                </Form.Item>

                <Form.Item label="Last Updated">
                    {moment(this.domain.updated_at).format('LLL')}
                    <span className="lowlight"> by </span>
                    {this.domain.updated_by_user_name}
                </Form.Item>
            </Well.Section>
        )
    }

    protected renderConfigurationWell() {
        return (
            <Well.Section title="Platform Configuration">
                <Form.Item label="Time Zone">
                    {!this.domain.timezone ? (
                        'None selected'
                    ) : (
                        <span>
                            {this.domain.timezone} | {moment().tz(this.domain.timezone).format('z | Z')}
                        </span>
                    )}
                </Form.Item>

                <Form.Item
                    label="Currency"
                    extra={
                        <span>
                            <InfoCircleOutlined /> Domain currency cannot be changed once set.
                        </span>
                    }
                >
                    {!this.domain.currency ? (
                        'None selected'
                    ) : (
                        <span>
                            {this.domain.currency.name} | {this.domain.currency_code}
                        </span>
                    )}
                </Form.Item>

                <Form.Item
                    label="SDK Allowed Domains"
                    extra={
                        <span>
                            <WarningOutlined /> Wildcard <b>*</b> entries are not supported by Apple's APNs and will
                            cause package failures for Safari integrations.
                        </span>
                    }
                >
                    {this.domain.whitelist_domains?.join(', ') ?? '*'}
                </Form.Item>

                {this.domainHasEventOnlyDomainsFlag && (
                    <Form.Item
                        label="SDK Event Only Domains"
                        extra={
                            <span>
                                <WarningOutlined /> All domains must also be included in the SDK Allowed Domains.
                                Wildcard <b>*</b> entries are not supported for event only domains.
                            </span>
                        }
                    >
                        {this.domain.sdk_event_only_domains.join(', ') || 'N/A'}
                    </Form.Item>
                )}
            </Well.Section>
        )
    }

    protected get domain(): DomainDto {
        return this.props.domain
    }

    protected get domainHasEventOnlyDomainsFlag(): boolean {
        return this.props.domain.flags.includes(FEAT_SDK_EVENT_ONLY_DOMAINS) ?? false
    }

    protected showNewTestForm = () => {
        this.setState({ showTestForm: true })
    }

    protected hideNewTestForm = () => {
        this.setState({ showTestForm: false })
    }

    protected reclone = async () => {
        const { domain } = this.props
        Modal.confirm({
            title: 'Refresh Demo Domain',
            icon: <GlobalOutlined />,
            okText: 'Refresh',
            content: (
                <p>
                    Are you sure you want to refresh <b>{domain.display_name}</b>? All existing content will be
                    replaced with a fresh clone from the template. This cannot be undone.
                </p>
            ),
            onOk: async () => {
                const { ok } = await this.domainService.recloneDomain(domain.id, { showLoadingScreen: true })
                if (ok) {
                    this.props.onDomainUpdated({})
                }
            },
        } as any)
    }

    protected reseedStats = async () => {
        const { domain } = this.props
        Modal.confirm({
            title: 'Refresh Demo Stats',
            icon: <BarChartOutlined />,
            okText: 'Refresh Stats',
            content: (
                <p>
                    Regenerate synthetic stats for <b>{domain.display_name}</b>? Existing stats will be replaced
                    with fresh data. Content and settings are not affected.
                </p>
            ),
            onOk: async () => {
                await this.domainService.reseedDomainStats(domain.id, { showLoadingScreen: true })
            },
        } as any)
    }

    protected resetStats = async (demoId: number | string) => {
        Modal.confirm({
            title: 'Reset Demo Stats',
            content: <div>Are you sure you wish to reset all stats associated with this demo domain?</div>,
            onOk: async () => {
                await this.domainService.resetDemoStats(demoId, {
                    showLoadingScreen: true,
                    cancellationKey: `demoStatsReset.${demoId}`,
                })
            },
        })
    }

    protected resetFull = async (demoId: number | string) => {
        Modal.confirm({
            title: 'Reset Demo Domain',
            content: (
                <div>
                    Are you sure you wish to fully reset this demo domain? This action will reset all associated
                    entities and cannot be reversed
                </div>
            ),
            onOk: async () => {
                await this.domainService.resetDemoDomain(demoId, {
                    showLoadingScreen: true,
                    cancellationKey: `demoReset.${demoId}`,
                })
            },
        })
    }

    protected buildDomainUrl(domain: DomainDto): string {
        return `/domains/${domain.id}`
    }

    protected jumpToDomain(domain: DomainDto): void {
        this.appService.route(this.buildDomainUrl(domain))
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }

    protected handleCopied = (field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }

    protected handleTestDomainCreate = (domain: DomainDto) => {
        this.jumpToDomain(domain)
    }

    protected async fetchAllIndustries(): Promise<void> {
        const { ok, data: industries } = await this.apiEnumService.fetchAllIndustries({
            query: {
                pagination: 0,
            },
            showLoadingScreen: false,
        })

        if (ok) {
            this.setState({ industries })
        }
    }
}
